import {DateRangeStartEnd, DaysOfWeek} from 'common/src/modules/adScheduling/adScheduling.types';
import {LandingPageSource} from 'common/src/modules/campaigns/campaigns.enums';
import {FCCampaign, FCServerCampaign} from 'common/src/modules/campaigns/campaigns.types';
import {
  ConditionCategory,
  ConditionOperatorKey,
  ConditionRange,
  ConditionType,
} from 'common/src/modules/conditions/conditions.types';
import {CreativeNodeData} from 'common/src/modules/creatives/creatives.types';
import {LocationFeed} from 'common/src/modules/locationFeed/locationFeed.types';
import {MacroItem, MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import {CampaignOptimizationData} from 'common/src/modules/optimization/optimization.types';
import {ProductFeed} from 'common/src/modules/productFeed/productFeed.types';
import {TimezoneObject} from 'common/src/modules/timezones/timezones.types';
import {PreviewStep, PreviewStepDetails} from 'common/src/modules/decisionTree/preview.types';
import {NodeInsights} from 'common/src/modules/campaignAnalytics/campaignAnalytics.types';
import {DateTime} from 'luxon';
import {ReactNode} from 'react';
import {
  CarouselProductSelectionType,
  FacebookAdType,
  FacebookSocialAdData,
  SocialAdData,
  SocialAdType,
} from 'common/src/modules/externalAds/externalAds.types';
import {NodesHeight} from 'common/src/components/CLCardsMap/Canvas/Canvas.types';
import {
  MapNode,
  NodeOutputData,
  NodeType,
  OutputType,
  ServerNode,
} from 'common/src/modules/nodes/nodes.types';
import {
  CardsMapCanvas,
  CardsMapZoom,
  NodeWrapperProps,
  SetupNodeActions,
} from 'common/src/components/CLCardsMap/CardsMapContainer/CardsMapContainer.types';
import {SocialNetworkPage} from 'common/src/modules/social/social.types';
import {MetricValue} from '../analytics/analytics.types';
import {ProductType} from '../products/products.enums';
import {LocationSet} from '../locationSets/locationSets.types';
import {ContentSelectionStrategyValue} from '../contentFeeds/contentFeeds.enums';

export type FCServerCampaignNode = {
  campaign: FCServerCampaign;
  x: number;
  y: number;
};

export type FCCampaignNode = Omit<FCServerCampaignNode, 'campaign'>;

export type ServerDecisionTree = {
  rootNodeId: number;
  nodes: ServerNode[];
  nextNodeId: number;
  fallbackCreativeNodeId: number;
  campaignNode: FCServerCampaignNode;
};

export interface ServerCampaignWithDT {
  loadedVersion: ServerCampaignVersion;
}

export type CampaignWithDT = {
  campaign: FCCampaign;
  nodes: DTNode[];
  loadedVersion: DTVersion;
  nodesOrder: number[];
  nextNodeId: number;
  rootNodeId: number;
  fallbackCreativeNodeId: number;
  campaignNode: FCCampaignNode;
};

export interface DTZoom extends CardsMapZoom {
  moveCampaignNode?: boolean;
}

// General export types

export type DTNode = MapNode & {
  isFallbackCreative?: boolean;
};

export type Nodes = Record<number, DTNode>;

export type ModeComponentProps = CardsMapCanvas & {
  isMiniMap?: boolean;
};

export type SetupNodeWrapperSpecificProps = SetupNodeActions &
  NodeWrapperProps & {
    connectionState: boolean;
    highlightSelectedNodes: boolean;
    outputConnectionNodeId: number;
    isNodeCollapsed: boolean;
    isNodeExpanding: boolean;
    isNodePreSelected: boolean;
    isOutOfScreen: boolean;
    selectableNodes: number[];
    noNodeSizeTransition: boolean;
    showIssues: boolean;
    getNodeContent?: (node: DTNode) => ReactNode;
    suppressNodeTypeSelection?: boolean;
    suppressFallbackOutputs?: boolean;
    forcedConnectionColor: string;
  };

export type PreviewNodeClickedParams = {
  nodeId: number;
  ctrlShiftPressed?: boolean;
  nodePositionX?: number;
  nodePositionY?: number;
  isDoubleClick?: boolean;
};

export type PreviewNodeWrapperSpecificProps = NodeWrapperProps & {
  onPreviewNodePressed: (params: PreviewNodeClickedParams) => void;
  onHoveredNodeChange: (hoveredNodeId: number) => void;
  isPreviewShown: boolean;
  previewStep?: PreviewStep;
  nodeMetadata?: PreviewStepDetails;
  childrenOfPreviewSteps: number[];
};

export type InsightsNodeWrapperSpecificProps = NodeWrapperProps & {
  outgoingInsights?: NodeInsights;
  incomingInsights?: NodeInsights;
  insightMetric: MetricValue;
  onNodeClicked: (addToSelection: boolean, removeFromSelection: boolean) => void;
  onResetSelectedNodes: () => void;
  onNodeInsightsMetricOpen: (node: MapNode) => void;
  nodesHeight: NodesHeight;
  metrics: MetricValue[];
};

// CampaignNode

export type DTCampaignNode = Omit<DTNode, 'name' | 'description'> & {
  type: NodeType.CAMPAIGN;
};

// Condition Node

export type ServerConditionNode = DTNode & {
  categories: ConditionCategory[];
  condKey: string;
  condKeyType?: MacroItemTypeValue;
  condKeyUIName?: string;
  signalUIName?: string;
  condOperatorUIName?: string;
  condKeyTypeUiName?: string;
  CampaignStatus?: string;
  type: NodeType.CONDITION;
  randomizeOutputs?: boolean;
  condOperator: ConditionOperatorKey;
  createAllVariations?: boolean;
  outputs: ServerConditionNodeOutputData[];
  signalDescription: string;
};

export type ServerConditionNodeOutputData = NodeOutputData & {
  type: OutputType;
  datesRange?: {from: string; to: string};
  numbersRange?: {from: string; to: string};
  textValues?: string[];
  numericValues?: number[];
  dateValues?: string[];
  booleanValue?: boolean;
};

export type ConditionNodeOutputData = Omit<
  ServerConditionNodeOutputData,
  'datesRange' | 'numbersRange' | 'dateValues'
> & {
  datesRange?: ConditionRange<DateTime>;
  numbersRange?: ConditionRange<number>;
  dateValues?: DateTime[];
};

export type DTConditionNode = Omit<ServerConditionNode, 'outputs'> & {
  outputs: ConditionNodeOutputData[];
  conditionType: ConditionType;
};

export interface RemoteCompoundConditionNode extends DTNode {
  outputs: RemoteCompoundConditionNodeOutputData[];
  randomizeOutputsEvaluationOrder: boolean;
  createAllVariations?: boolean;
}

// compound condition node
export interface DTCompoundConditionNode extends Omit<RemoteCompoundConditionNode, 'outputs'> {
  outputs: CompoundConditionNodeOutputData[];
}

export interface RemoteCompoundConditionNodeOutputData extends NodeOutputData {
  sections: RemoteCompoundConditionNodeSection[];
}

export interface CompoundConditionNodeOutputData
  extends Omit<RemoteCompoundConditionNodeOutputData, 'sections'> {
  sections: CompoundConditionNodeSection[];
  isExpanded?: boolean;
}

export interface RemoteCompoundConditionNodeSection {
  rules: RemoteCompoundConditionNodeRule[];
}

export interface CompoundConditionNodeSection
  extends Omit<RemoteCompoundConditionNodeSection, 'rules'> {
  rules: CompoundConditionNodeRule[];
}

export interface RemoteCompoundConditionNodeRule {
  signal: CompoundConditionSignal;
  ruleOperator: number;
  ruleValue: RemoteCompoundConditionNodeValue;
}

export type CompoundConditionSignal = MacroItem & {categories: number[]};

export interface CompoundConditionNodeRule
  extends Omit<RemoteCompoundConditionNodeRule, 'ruleValue'> {
  ruleValue: CompoundConditionNodeValue;
}

export interface RemoteCompoundConditionNodeValue {
  textValues?: string[];
  dateValues?: string[];
  numericValues?: number[];
  booleanValue?: boolean;
  datesRange?: {from: string; to: string};
  numbersRange?: {from: number; to: number};
  signal?: CompoundConditionSignal;
}

export interface CompoundConditionNodeValue
  extends Omit<RemoteCompoundConditionNodeValue, 'datesRange' | 'dateValues'> {
  datesRange?: {from: DateTime; to: DateTime};
  dateValues?: DateTime[];
}

// Weighted Node

export type WeightedNodeOutputData = NodeOutputData & {
  weight: number;
};

export type DTWeightedNode = DTNode & {
  type: NodeType.WEIGHTED;
  shouldSplitAudience: boolean;
  outputs: WeightedNodeOutputData[];
};

// Distribution Node

export type DTDistributionNode = DTNode & {
  type: NodeType.DISTRIBUTION;
  outputs: NodeOutputData[];
};

// Sequence Node

export type DTSequenceNode = DTNode & {
  type: NodeType.SEQUENCE;
  maxRepeats?: number;
  outputs: SequenceNodeOutputData[];
};

export type SequenceNodeOutputData = NodeOutputData & {
  maxRepeats?: number;
};

// Product Selection
export type ProductSelectionNodeOutputData = NodeOutputData & {
  type?: OutputType;
};

export type ServerItemsLogic = {
  id: number;
  name: string;
  items_count: number;
};

export type ItemsLogic = Omit<ServerItemsLogic, 'items_count'> & {
  itemsCount: number;
};

export type ServerDTProductSelectionNode = DTNode & {
  type: NodeType.PRODUCTS;
  productsSelectionStrategy: number;
  feedId: number;
  topItemLogicId: number;
  topItemLogic?: ServerItemsLogic;
  retargetingProductSetId: number;
  retargetingProductSetLogic?: ServerItemsLogic;
  categoryTopItemsLogicId: number;
  categoryTopItemsLogic?: ServerItemsLogic;
  recommendationSources?: number[];
  feed?: ProductFeed;
  outputs: ProductSelectionNodeOutputData[];
  configuration?: {
    conditions?: ProductSelectionCondition[];
    orderBy?: string;
    selectionOrder?: ProductSortingOrder;
    contentType: number;
  };
  feedDescription?: string;
  strategyDescription?: string;
  teamNameSourceSignalKey?: string;
};

export interface ServerCarouselNode extends Omit<DTCarouselNode, 'cardsInfo'> {
  cardsInfo: ServerFBCarouselCreative[];
}

interface ServerFBCarouselCreative extends Omit<FBCarouselCreative, 'creative'> {
  creative: CreativeNodeData;
}

export interface ServerDTCreativeNode extends Omit<DTCreativeNode, 'creative'> {
  creative: CreativeNodeData;
}

export interface ServerSocialCreativeNode extends Omit<DTSocialCreativeNode, 'creative'> {
  creative: CreativeNodeData;
}

export interface ServerNativeCreativeNode extends Omit<DTNativeCreativeNode, 'creative'> {
  creative: CreativeNodeData;
}

export enum ProductMatchingOperator {
  Equals = 1,
  Contains = 2,
  ContainedIn = 3,
  GreaterThanOrEqual = 4,
  LessThanOrEqual = 5,
}

export enum ProductMatchingOperatorDescription {
  Equals = 'Equals',
  Contains = 'Contains',
  ContainedIn = 'Contained in',
  GreaterThanOrEqual = 'Greater than or equal',
  LessThanOrEqual = 'Less than or equal',
}

export enum ProductSortingOrder {
  Random = 0,
  Descending = 1,
  Ascending = 2,
}

export type ServerCreativeNode =
  | ServerDTCreativeNode
  | ServerSocialCreativeNode
  | ServerNativeCreativeNode;

export interface ProductSelectionCondition {
  feedColumnSignalKey: string;
  operator: ProductMatchingOperator;
  signalKey: string;
  feedColumnUIName: string;
  signalMainCategory?: ConditionCategory;
  signalUIName: string;
}

export interface ProductSelectionConfiguration {
  conditions: ProductSelectionCondition[];
  orderBy: number;
  selectionOrder?: ProductSortingOrder;
  contentType: number;
}

export interface ViewedItemsConfiguration {
  frequencyPower: number;
  recencyPower: number;
  pageScorePower: number;
  allPages: boolean;
  pagesWhitelist: number[];
  allDataSources: boolean;
  dataSourcesWhitelist: number[];
  lookbackWindowDays: number;
  engagementRepeatsInfluence: number;
}

export type DTProductSelectionNode = Omit<
  ServerDTProductSelectionNode,
  'topItemLogic' | 'categoryTopItemsLogic' | 'retargetingProductSetLogic' | 'configuration'
> & {
  topItemLogic?: ItemsLogic;
  categoryTopItemsLogic?: ItemsLogic;
  retargetingProductSetLogic?: ItemsLogic;
  configuration?: ProductSelectionConfiguration;
  productIds?: string[];
  retargetingSettings?: ViewedItemsConfiguration;
};

// Store Node

export type StoreNodeOutputData = NodeOutputData & {
  type: OutputType;
  values?: string[];
};

export type DTStoreNode = DTNode & {
  type: NodeType.STORE;
  locationFeed?: LocationFeed;
  locationFeedId: number;
  locationSetId?: number;
  locationSet?: LocationSet;
  locationTargetingMethod: number;
  maxDistFallbackOption: number;
  maxDistKm: number;
  topLocationIds: string[];
  outputs: StoreNodeOutputData[];
  feedDescription?: string;
};

// CreativeOptimization Node

export type OptimizationNodeOutputData = NodeOutputData & {
  type: OutputType;
  node?: DTCreativeNode | DTProductSelectionNode;
};

export type DTOptimizationNode = DTNode & {
  type: NodeType.GENERAL_OPTIMIZATION;
  configuration?: CampaignOptimizationData;
  variationsCount?: number;
  outputs: OptimizationNodeOutputData[];
};

// Schedule Node

export type ScheduleNodeOutputData = NodeOutputData & {
  dateRange?: DateRangeStartEnd;
  daysOfWeek?: Array<DaysOfWeek>;
  type: OutputType;
};

export enum ScheduleType {
  DateRange = 10,
  DaysOfWeek = 20,
}

export enum ScheduleTypeDescription {
  DateRange = 'Date range',
  DaysOfWeek = 'Days of week',
}

export type DTScheduleNode = DTNode & {
  type: NodeType.SCHEDULE;
  outputs: ScheduleNodeOutputData[];
  scheduleType: ScheduleType;
  timezoneSource: number;
  timeZone: string;
  timeZoneDetails?: TimezoneObject;
  randomizeOutputs?: boolean;
};

export type ServerScheduleNode = DTNode & {
  type: NodeType.SCHEDULE;
  outputs: ScheduleNodeServerOutputData[];
  scheduleType: ScheduleType;
  timezoneSource: number;
  timeZone: string;
  randomizeOutputs?: boolean;
  timeZoneDetails?: TimezoneObject;
};

export type ScheduleNodeServerOutputData = NodeOutputData & {
  daysOfWeek?: Array<DaysOfWeek>;
  type: OutputType;
  dateRange?: {start: string; end: string};
};

// Creative Node
export type DTCreativeNode = DTNode &
  CreativeNodeParams & {
    type: NodeType.CREATIVE;
  };

export type SocialCreativeIdentityAndTracking = {
  identity?: SocialNetworkPage;
  additionalIdentity?: SocialNetworkPage;
  trackingPixel?: SocialNetworkPage;
};

export type DoubleClickCampaignManagerParams = {
  dcmTrackingAdId?: string;
  dcmTrackingCreativeId?: string;
  dcmTrackingPlacementId?: string;
};

export type DTSocialCreativeNode = DTNode &
  SocialCreativeIdentityAndTracking &
  CreativeNodeParams &
  SocialAdData & {
    displayUrl: string;
    adNameTemplate?: string;
    type:
      | NodeType.SOCIAL_FACEBOOK_AD_CREATIVE
      | NodeType.SOCIAL_YOUTUBE_AD_CREATIVE
      | NodeType.SOCIAL_TIKTOK_AD_CREATIVE
      | NodeType.SOCIAL_SNAPCHAT_AD_CREATIVE
      | NodeType.SOCIAL_LINKEDIN_AD_CREATIVE
      | NodeType.SOCIAL_YOUTUBE_DV360_AD_CREATIVE;
    adType?: SocialAdType;
    //only for snapchat will be moved to different type after social creative refactor
    brandName?: string;
    isShareable?: boolean;
    isStandardButtonColor?: boolean;
    isPublicPosts?: boolean;
    mediaNameTemplate?: string;
    clickTrackers?: string[];
    impressionTrackers?: string[];
    //only for youtube dv360 campaigns
    companionBannerAssetID?: string;
    doubleVerifyTracking?: boolean;
    doubleVerifyMeasure?: boolean;
  } & DoubleClickCampaignManagerParams; //for youtube dv360 campaigns;

export type DTFacebookStandardCreativeNode = DTNode &
  SocialCreativeIdentityAndTracking &
  Omit<CreativeNodeParams, 'utmParams' | 'images' | 'requireLocation' | 'requireProduct'> &
  FacebookSocialAdData & {
    displayUrl: string;
    adNameTemplate: string;
    mediaNameTemplate: string;
    type: NodeType.SOCIAL_FACEBOOK_AD_CREATIVE;
    adType: FacebookAdType;
  };

// this type will be updated when we will have more refactored social creative nodes
export type DTSocialCampaignCreativeNode = DTFacebookStandardCreativeNode;

export type DTBaseCarouselNode = DTNode &
  SocialAdData &
  SocialCreativeIdentityAndTracking &
  CarouselParams & {
    adNameTemplate?: string;
    displayUrl?: string;
    landingPage: string;
    landingPageSource: LandingPageSource;
    utmParams: string;
    productSelectionType?: CarouselProductSelectionType;
    receivedContent?: {
      ReceivedProductType: number;
    };
  };

export type DTCarouselNode = DTNode &
  SocialCreativeIdentityAndTracking &
  DTBaseCarouselNode & {
    externalAdPrimaryText: string[];
    externalAdCTA: string;
    type: NodeType.FACEBOOK_CAROUSEL_CREATIVE;
    cardsInfo: FBCarouselCreative[];
    landingPageSource: LandingPageSource;
    landingPage?: string;
    adNameTemplate?: string;
    displayUrl?: string;
    productSelectionType: CarouselProductSelectionType;
  };

export type DPANode = {
  catalogId?: string;
  productSetId?: string;
  outputs: NodeOutputData[];
};

export type CarouselParams = {
  displayCarouselCardsAsVideo: boolean;
  bestPerformingCardsFirst: boolean;
  addPageProfileCard: boolean;
  addMusic: boolean;
  displayCarouselCardsAsSlideshow: boolean;
};

export type RemoteDPACreativeNode = DTNode &
  DPANode &
  CarouselParams &
  SocialCreativeIdentityAndTracking &
  FacebookSocialAdData & {
    type: NodeType.FACEBOOK_DPA_CREATIVE;
    displayUrl: string;
    landingPage: string;
    landingPageSource: LandingPageSource;
    utmParams: string;
    previewItemUrls: string[];
    adNameTemplate: string;
    mediaNameTemplate: string;
    receivedContent: {
      ReceivedProductType: ProductType;
    };
    additionalImageIndex: number;
  };

export type DPACreativeNode = RemoteDPACreativeNode;

export type RemoteDPACarouselCreativeNode = Omit<RemoteDPACreativeNode, 'type'> &
  DTBaseCarouselNode & {
    type: NodeType.FACEBOOK_DPA_CAROUSEL_CREATIVE;
    isMultipleProducts: boolean;
  };

export type DPACarouselCreativeNode = RemoteDPACarouselCreativeNode;

export type DTNativeCreativeNode = DTNode &
  CreativeNodeParams &
  NativeAdData & {
    type: NodeType.NATIVE_CREATIVE;
  };

export type CreativeNode = DTCreativeNode | DTSocialCreativeNode | DTNativeCreativeNode;
export const CreativeNodePlaceholderCreativeId = 0;

export type ContentFeedNode = DTNode & {
  type: NodeType.CONTENT_FEED;
  contentFeed?: {
    id: number;
    name: string;
    itemCount: number;
  };
  contentFeedId: number;
  contentId?: string;
  configuration?: ProductSelectionConfiguration;
  timezoneSource: number;
  timeZone: string;
  strategy?: ContentSelectionStrategyValue;
  contentSetId?: number;
  contentSet?: {
    id: number;
    name: string;
    itemCount: number;
  };
};

export type NativeAdData = {
  nativeAdTitle?: string;
  nativeAdDescription?: string;
  nativeAdSponsor?: string;
  nativeAdCTA?: string;
  nativeAdLogo?: string;
  nativeAdLogoSize?: number;
};

export type CreativeNodeParams = {
  creative: CreativeNodeData;
  creativeId: number;
  landingPage?: string;
  landingPageSource: LandingPageSource;
  utmParams?: string;
  receivedContent?: {
    ReceivedProductType: number;
  };
  images?: string[];
  requireLocation?: boolean;
  requireProduct?: boolean;
};

export type FBCarouselCreative = {
  landingPage: string;
  landingPageSource: LandingPageSource;
  description: string;
  headline: string;
  creativeId: number;
  creative: CreativeNodeData;
  mediaNameTemplate: string;
};

export interface ScheduleTiming {
  cronExpression: string;
  timeZone: string;
}

export type DTVersion = {
  name: string;
  number: number;
  id: number;
  lastUpdate?: DateTime;
  createdDate?: DateTime;
  savedDate?: DateTime;
  isDraft?: boolean;
  isActivated?: boolean;
  isPendingActivation?: boolean;
  description?: string;
  activationTime: {start: DateTime | null; end: DateTime | null};
  statusChangedBy?: string;
  placementsCount?: number;
  hasConceptPlan: boolean;
  isContextualAllowed?: boolean;
  nodesCount?: number;
};

export type ServerCampaignVersion = {
  decisionTree: ServerDecisionTree;
  description: string;
  isActivated: boolean;
  isDraft: boolean;
  isPendingActivation: boolean;
  lastUpdate: string;
  createdDate: string;
  savedDate: string;
  name: string;
  id: number;
  number: number;
  activationStart: string;
  activationEnd: string;
  statusChangedBy?: string;
  placementsCount?: number;
  hasConceptPlan: boolean;
  isContextualAllowed?: boolean;
  nodesCount?: number;
};

export type SocialCreativeNodeType =
  | NodeType.SOCIAL_FACEBOOK_AD_CREATIVE
  | NodeType.SOCIAL_TIKTOK_AD_CREATIVE
  | NodeType.SOCIAL_YOUTUBE_AD_CREATIVE
  | NodeType.SOCIAL_YOUTUBE_DV360_AD_CREATIVE
  | NodeType.SOCIAL_SNAPCHAT_AD_CREATIVE
  | NodeType.SOCIAL_LINKEDIN_AD_CREATIVE;
